/**
 * useMonitoringOverview — Level 1 data for the Outcomes Monitoring Report.
 *
 * GET /v1/datasets/{id}/outcomes-report/overview?framework=cd|tcf&<filters>
 */

import { useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import type { FilterConfig } from '../../../records/filter';
import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams } from '../../cause-report/utils';
import { cleanOutcomeName } from '../utils';
import { MONITORING_STALE_TIME, monitoringKeys } from '../constants';
import type {
  AggregateBlock,
  Bands,
  DetrimentMix,
  Framework,
  OutcomeCardData,
  OverviewData,
} from '../types';

interface BandsApi {
  low: number;
  mod: number;
  high: number;
}
interface DetrimentMixApi {
  dissatisfaction: number;
  strain: number;
  harm: number;
}
interface AggregateApi {
  score: number;
  score_prev: number | null;
  volume: number;
  volume_prev: number | null;
  coverage_pct: number;
  bands: BandsApi;
  detriment_mix: DetrimentMixApi;
  detriment_mix_prev: DetrimentMixApi | null;
}
interface OutcomeCardApi {
  id: string;
  name: string;
  score: number;
  score_prev: number | null;
  volume: number;
  volume_prev: number | null;
  bands: BandsApi;
  detriment_mix: DetrimentMixApi;
}
interface OverviewApiResponse {
  framework: string;
  aggregate: AggregateApi;
  outcomes: OutcomeCardApi[];
}

const bands = (b: BandsApi): Bands => ({ low: b.low, mod: b.mod, high: b.high });
const mix = (m: DetrimentMixApi): DetrimentMix => ({
  dissatisfaction: m.dissatisfaction,
  strain: m.strain,
  harm: m.harm,
});

function transform(data: OverviewApiResponse): OverviewData {
  const a = data.aggregate;
  const aggregate: AggregateBlock = {
    score: a.score,
    scorePrev: a.score_prev,
    volume: a.volume,
    volumePrev: a.volume_prev,
    coveragePct: a.coverage_pct,
    bands: bands(a.bands),
    detrimentMix: mix(a.detriment_mix),
    detrimentMixPrev: a.detriment_mix_prev ? mix(a.detriment_mix_prev) : null,
  };
  const outcomes: OutcomeCardData[] = data.outcomes.map((o) => ({
    id: o.id,
    name: cleanOutcomeName(o.name),
    score: o.score,
    scorePrev: o.score_prev,
    volume: o.volume,
    volumePrev: o.volume_prev,
    bands: bands(o.bands),
    detrimentMix: mix(o.detriment_mix),
  }));
  return { framework: data.framework, aggregate, outcomes };
}

async function fetchOverview(
  datasetId: string,
  framework: Framework,
  params: Record<string, string>,
): Promise<OverviewApiResponse> {
  const qs = new URLSearchParams({ framework, ...params }).toString();
  return fetchWithAuth(`/v1/datasets/${datasetId}/outcomes-report/overview?${qs}`);
}

export function useMonitoringOverview(
  datasetId: string,
  framework: Framework,
  filters: FilterConfig,
) {
  const filterParams = useMemo(() => mapFiltersToApiParams(filters), [filters]);

  const query = useQuery({
    queryKey: monitoringKeys.overview(datasetId, framework, filterParams),
    queryFn: () => fetchOverview(datasetId, framework, filterParams),
    select: transform,
    staleTime: MONITORING_STALE_TIME,
    enabled: !!datasetId,
    // Keep the previous filter's data on screen while the new one loads, so a
    // filter change dims-and-refreshes (isFetching, isLoading false) instead of
    // blanking to skeletons on every change. First load still has no previous
    // data, so it shows skeletons as before.
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    // isFetching is true during a background refetch (e.g. a filter change),
    // when isLoading is false because cached data is already on screen. The page
    // uses it to show a "refreshing" state instead of leaving stale figures up.
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
